'use client';

import { clsx } from 'clsx';
import { ComponentProps, FC, ReactNode } from 'react';

import { IconHeart } from 'src/general/Icons/IconHeart';
import { IconHeartFilled } from 'src/general/Icons/IconHeartFilled';
import { ProductListDataRow } from 'src/types/CataloguePage.types';

import { Button } from '../Button/Button';
import { useToggleWatchList } from './hooks/useToggleWatchList';

type LabelsOverwrite = {
  save: ReactNode;
  saved: ReactNode;
};

type AddToWatchListButtonProps = {
  sku: string;
  className?: string;
  labelsOverwrite?: LabelsOverwrite;
  disableSnackbar?: boolean;
  product?: Partial<ProductListDataRow>;
  onWatchlistStatusChange?: () => Promise<void>;
} & {
  buttonProps?: Pick<ComponentProps<typeof Button>, 'variant' | 'fullWidth' | 'size'>;
};

export const AddToWatchListButton: FC<AddToWatchListButtonProps> = ({
  sku,
  className,
  labelsOverwrite = {
    save: 'Save',
    saved: 'Saved',
  },
  product = {},
  onWatchlistStatusChange,
  disableSnackbar = false,
  ...props
}) => {
  const { userAddedItem, handleToggle, isLoading } = useToggleWatchList(
    sku,
    disableSnackbar,
    onWatchlistStatusChange,
    product,
  );

  const buttonLabel = userAddedItem ? labelsOverwrite.saved : labelsOverwrite.save;

  return (
    <Button
      size="small"
      {...props.buttonProps}
      onClick={handleToggle}
      data-testid={`AddToWatchList-component-button-${userAddedItem ? 'Saved' : 'Save'}`}
      startIcon={
        userAddedItem ? (
          <span className="text-pin-500">
            <IconHeartFilled />
          </span>
        ) : (
          <IconHeart />
        )
      }
      className={clsx('watchlistButton', className)}
      disabled={isLoading}
    >
      {buttonLabel}
    </Button>
  );
};
